import type { Profile } from "@/types";
import { PLATFORM_TRIAL_DAYS } from "@/lib/platform-trial-days";
import { needsPostPromoPlanSelection, promoGrantedTier } from "@/lib/owner-promo-plan-pick";

export { PLATFORM_TRIAL_DAYS };

/** True only when `OWNER_EMAIL` is set and matches (case-insensitive). Never rely on client-supplied flags. */
export function isOwnerAccount(email: string | null | undefined): boolean {
  const ownerEmail = process.env.OWNER_EMAIL?.trim().toLowerCase();
  return Boolean(ownerEmail) && (email ?? "").trim().toLowerCase() === ownerEmail;
}

export function hasActivePlan(subscriptionStatus: string | null | undefined): boolean {
  const status = (subscriptionStatus ?? "").trim().toLowerCase();
  return status === "active" || status === "trialing";
}

/**
 * App-access check with a dunning grace period. Stripe flips a subscription
 * to `past_due` on the FIRST failed charge and then retries for days before
 * settling on `canceled`/`unpaid`. Locking instantly on `past_due` meant a
 * paying customer with an expired card lost quotes, PDF export, and AI
 * mid-job before Stripe had even finished retrying. So: keep access during
 * `past_due` (the payment-failed banner still shows via
 * `billingBannerFromCompanyProfile`, which uses the strict check above) and
 * lock only on terminal statuses. Metrics/owner dashboards keep using
 * `hasActivePlan` so past_due accounts aren't counted as cleanly paying.
 */
export function planStatusAllowsAppAccess(subscriptionStatus: string | null | undefined): boolean {
  const status = (subscriptionStatus ?? "").trim().toLowerCase();
  return status === "active" || status === "trialing" || status === "past_due";
}

/** Owner-issued promo: when `owner_promo_trial_ends_at` is in the future, app access rules differ from the default platform trial. */
export function isOwnerPromoTrialActive(ownerPromoTrialEndsAt?: string | null): boolean {
  if (!ownerPromoTrialEndsAt) return false;
  const t = new Date(ownerPromoTrialEndsAt).getTime();
  return Number.isFinite(t) && t > Date.now();
}

/** First N days after profile creation: full app access without a platform subscription or card. */
const MS_PER_DAY = 86400000;

export function isWithinPlatformTrial(accountCreatedAt?: string | null): boolean {
  const end = platformTrialEndsAt(accountCreatedAt);
  if (!end) return false;
  return Date.now() < end.getTime();
}

export function platformTrialEndsAt(accountCreatedAt?: string | null): Date | null {
  if (!accountCreatedAt) return null;
  const created = new Date(accountCreatedAt).getTime();
  if (!Number.isFinite(created)) return null;
  return new Date(created + PLATFORM_TRIAL_DAYS * MS_PER_DAY);
}

/** Full calendar days remaining until `end` (minimum 0). */
export function wholeDaysRemainingUntil(end: Date | null): number | null {
  if (!end) return null;
  const ms = end.getTime() - Date.now();
  if (ms <= 0) return 0;
  return Math.ceil(ms / MS_PER_DAY);
}

export type BillingGateProfile = Pick<
  Profile,
  | "subscription_status"
  | "stripe_subscription_id"
  | "owner_promo_trial_ends_at"
  | "owner_promo_granted_tier"
  | "owner_promo_plan_confirmed_at"
  | "subscription_trial_end_at"
  | "created_at"
> & {
  /** Anchor for the 3-day platform trial. Falls back to `created_at` when null. */
  platform_trial_started_at?: string | null;
};

/**
 * Returns the timestamp the platform trial should be measured from.
 * Falls back to `created_at` so existing accounts (and back-compat selects
 * that don't yet include `platform_trial_started_at`) behave unchanged.
 */
export function platformTrialAnchor(
  profile: { created_at?: string | null; platform_trial_started_at?: string | null }
): string | null {
  return profile.platform_trial_started_at ?? profile.created_at ?? null;
}

export function billingAccessFromProfile(profile: BillingGateProfile, userEmail: string | null | undefined) {
  return {
    email: userEmail,
    subscriptionStatus: profile.subscription_status,
    stripeSubscriptionId: profile.stripe_subscription_id,
    ownerPromoTrialEndsAt: profile.owner_promo_trial_ends_at ?? null,
    ownerPromoGrantedTier: profile.owner_promo_granted_tier ?? null,
    ownerPromoPlanConfirmedAt: profile.owner_promo_plan_confirmed_at ?? null,
    accountCreatedAt: platformTrialAnchor(profile),
  };
}

export function canCreateQuotesNow(options: {
  email?: string | null;
  subscriptionStatus?: string | null;
  stripeSubscriptionId?: string | null;
  /** Platform-owner promo: grants access without Stripe until this instant. */
  ownerPromoTrialEndsAt?: string | null;
  ownerPromoGrantedTier?: string | null;
  ownerPromoPlanConfirmedAt?: string | null;
  /** Company profile `created_at`; unlocks app during the first `PLATFORM_TRIAL_DAYS` without a subscription. */
  accountCreatedAt?: string | null;
}): boolean {
  if (isOwnerAccount(options.email)) return true;

  if (
    needsPostPromoPlanSelection({
      stripe_subscription_id: options.stripeSubscriptionId,
      owner_promo_trial_ends_at: options.ownerPromoTrialEndsAt,
      owner_promo_granted_tier: options.ownerPromoGrantedTier,
      owner_promo_plan_confirmed_at: options.ownerPromoPlanConfirmedAt,
    })
  ) {
    return false;
  }

  const hasSub = Boolean((options.stripeSubscriptionId ?? "").trim());

  if (isOwnerPromoTrialActive(options.ownerPromoTrialEndsAt)) {
    // Owner-granted promos work WITHOUT a Stripe subscription (that's the
    // point of the grant), so `subscription_status` is null for these
    // accounts. Only defer to the status check when a subscription exists,
    // e.g. so a canceled sub isn't silently revived past dunning.
    if (!hasSub) return true;
    return planStatusAllowsAppAccess(options.subscriptionStatus);
  }

  if (hasSub) {
    return planStatusAllowsAppAccess(options.subscriptionStatus);
  }

  if (isWithinPlatformTrial(options.accountCreatedAt)) return true;
  return false;
}

/** Serializable banner payload for {@link billingBannerFromCompanyProfile} + `/api/billing/snapshot`. */
export type BillingBanner =
  | { kind: "platform_trial"; daysLeft: number }
  | { kind: "stripe_trialing"; daysLeft: number }
  | { kind: "owner_promo_trial"; daysLeft: number }
  | { kind: "promo_plan_pick"; highlightTier: "starter" | "solo" | "pro" | "agency" }
  | { kind: "trial_ended" }
  | { kind: "payment_failed" };

/**
 * Top-of-app messaging for billing (server + snapshot API). Omits banner when access is normal or owner bypass.
 */
export function billingBannerFromCompanyProfile(
  profile: BillingGateProfile & {
    stripe_connect_account_id?: string | null;
  },
  userEmail: string | null | undefined
): BillingBanner | null {
  if (isOwnerAccount(userEmail)) return null;

  const access = billingAccessFromProfile(profile, userEmail);
  const status = (profile.subscription_status ?? "").trim().toLowerCase();
  const hasSub = Boolean((profile.stripe_subscription_id ?? "").trim());

  if (hasSub && !hasActivePlan(profile.subscription_status)) {
    if (status === "past_due" || status === "unpaid") {
      return { kind: "payment_failed" };
    }
  }

  const noStripeSub = !Boolean((profile.stripe_subscription_id ?? "").trim());
  if (
    noStripeSub &&
    isOwnerPromoTrialActive(profile.owner_promo_trial_ends_at) &&
    canCreateQuotesNow(access)
  ) {
    const end = new Date(String(profile.owner_promo_trial_ends_at));
    const daysLeft = wholeDaysRemainingUntil(Number.isFinite(end.getTime()) ? end : null) ?? 0;
    return { kind: "owner_promo_trial", daysLeft };
  }

  const highlight = promoGrantedTier(profile);
  if (needsPostPromoPlanSelection(profile) && highlight) {
    return { kind: "promo_plan_pick", highlightTier: highlight };
  }

  if (!canCreateQuotesNow(access)) {
    return { kind: "trial_ended" };
  }

  /** Stripe subscription intro trial (not the owner-promo path, which is surfaced above). */
  if (
    !isOwnerPromoTrialActive(profile.owner_promo_trial_ends_at) &&
    hasSub &&
    status === "trialing"
  ) {
    const trialEndRaw = profile.subscription_trial_end_at ?? null;
    if (trialEndRaw) {
      const end = new Date(trialEndRaw);
      if (Number.isFinite(end.getTime()) && end.getTime() > Date.now()) {
        const daysLeft = wholeDaysRemainingUntil(end) ?? 0;
        return { kind: "stripe_trialing", daysLeft };
      }
    }
  }

  const trialAnchor = platformTrialAnchor(profile);
  if (
    !hasSub &&
    !isOwnerPromoTrialActive(profile.owner_promo_trial_ends_at) &&
    isWithinPlatformTrial(trialAnchor)
  ) {
    const end = platformTrialEndsAt(trialAnchor);
    const daysLeft = wholeDaysRemainingUntil(end) ?? 0;
    return { kind: "platform_trial", daysLeft };
  }

  return null;
}
